import React, { useEffect, useState } from "react";
import { materialService } from "../../firebase/services";
import {
  FileText,
  Download,
  ExternalLink,
  BookOpen,
  Loader2,
  AlertCircle,
  FileCode,
} from "lucide-react";

export const StudentMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const list = await materialService.getMaterials(true);
        setMaterials(list);
      } catch (err) {
        console.error("Materials load error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, []);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold tracking-tight">15 Days Masterclass Study Material</h1>
        <p className="text-xs text-muted-foreground font-mono">
          Download PDF lab handouts, command cheatsheets, and presentation notes published by instructors.
        </p>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
          <span className="text-xs font-mono">Loading published study resources...</span>
        </div>
      ) : materials.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground space-y-2 bg-card border border-border rounded-xl">
          <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/50" />
          <div className="text-sm font-semibold text-foreground">No study material available.</div>
          <p className="text-xs max-w-sm mx-auto font-mono">
            Instructors will upload lecture slides, PDF lab sheets, and security notes as each day's module progresses.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {materials.map((item) => (
            <div
              key={item.id}
              className="p-5 rounded-xl bg-card border border-border hover:border-emerald-500/40 transition-all flex flex-col justify-between space-y-4 shadow-sm"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-mono text-xs font-bold border border-emerald-500/20">
                    Day {item.dayNumber}
                  </span>
                  {item.fileSize && (
                    <span className="text-[11px] font-mono text-muted-foreground">
                      {item.fileSize}
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-sm text-foreground">{item.title}</h3>
                {item.description && (
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                )}
              </div>

              <div className="pt-2 border-t border-border flex items-center justify-between">
                <span className="text-[11px] font-mono text-muted-foreground truncate max-w-[180px]">
                  {item.fileName || "PDF Document"}
                </span>
                {item.fileUrl ? (
                  <a
                    href={item.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/90 hover:bg-emerald-500 text-white text-xs font-mono font-medium transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download PDF</span>
                  </a>
                ) : (
                  <span className="text-xs font-mono text-muted-foreground italic">
                    File linked in class
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
